// Pure classification of postMessage events from the PayPro Global checkout
// iframe. Extracted from SubscribePage so the filter logic can be unit-tested
// without instantiating the page (which pulls in Angular DI, services, etc.).
//
// Background: PPG's iframe posts a mix of message shapes:
//   - the JSON checkout protocol we actually care about:
//       {"type":"checkout_loaded"} / "checkout_resized" / "email_changed"
//   - PPG-internal frame plumbing (e.g. registerAsChildFrameAck) marked by
//       "command", "event_name", or "functionName"
//   - browser autofill bookkeeping marked by "functionName"
//
// Sentry 3S9 (UnexpectedCheckoutFrameMessage) keeps firing because PPG
// sometimes posts the internal-notification payload as a JavaScript object
// rather than a JSON string. The previous filter required
// `typeof ev.data === 'string'`, so object-form messages slipped past every
// filter and landed in the "unrecognised" branch. We normalise to a string
// up front so the filters cover both wire formats.

// Shape of a parsed PayPro checkout-frame message. The named protocol events
// (`checkout_loaded`, `checkout_resized`, `email_changed`) all share this
// shape, so the type is intentionally not a discriminated union — the call
// site branches on `type` as a string. Unknown `type` values fall through to
// trackSilentProblem('UnknownCheckoutFrameMessageType') at the call site.
export type CheckoutFrameMessage = { type: string; height?: number; value?: string };

export type CheckoutFrameMessageClassification =
  // origin not PayPro, or a known internal-frame notification we deliberately drop
  | { kind: 'ignore' }
  // looked like PayPro but not a JSON-shaped {"type":...} payload — report
  | { kind: 'unrecognised'; data: string }
  // JSON parsed cleanly — dispatch by `event.type` at the call site
  | { kind: 'event'; event: CheckoutFrameMessage; data: string }
  // JSON shape matched but JSON.parse still threw
  | { kind: 'parseError'; data: string; error: unknown };

// Exact origin string the PayPro checkout iframe posts from. `event.origin`
// is `scheme://host[:port]` only (no path), so an exact match is the right
// check. A prior `startsWith` form would have accepted look-alike hosts like
// `https://store.payproglobal.com.attacker.example`.
const PAYPRO_ORIGIN = 'https://store.payproglobal.com';

// Map any postMessage payload to a string the filters can match on. PPG may
// send a JSON string OR a plain JS object; both must be handled identically.
function normaliseFrameMessageData(data: unknown): string {
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data === 'object') {
    try {
      return JSON.stringify(data);
    } catch {
      // circular refs etc. — give the caller something inspectable in Sentry
      return '[unstringifiable object]';
    }
  }
  return String(data);
}

// Classify a postMessage event from the PayPro checkout iframe. Pure function:
// no DOM access, no logging, no side effects. The call site
// (SubscribePayProRail) is responsible for dispatch (markForCheck + per-type
// handling), Sentry reports, and ChangeDetection — this helper only decides
// which bucket the message belongs in.
export function classifyCheckoutFrameMessage(
  ev: { data: unknown; origin: string } | null | undefined
): CheckoutFrameMessageClassification {
  if (!ev || ev.origin !== PAYPRO_ORIGIN) {
    return { kind: 'ignore' };
  }

  const data = normaliseFrameMessageData(ev.data);

  // browser autofill tracking (e.g. updateAutofillAppliedStatusForTag)
  if (data.includes('"functionName"')) {
    return { kind: 'ignore' };
  }
  // PPG internal iframe command messages (e.g. registerAsChildFrameAck)
  if (data.includes('"command"')) {
    return { kind: 'ignore' };
  }
  // PPG internal iframe event notifications (e.g. IABPaypalButtonIframeFound)
  if (data.includes('"event_name"')) {
    return { kind: 'ignore' };
  }

  if (!data.startsWith('{') || !data.endsWith('}') || !data.includes('"type"')) {
    return { kind: 'unrecognised', data };
  }

  try {
    const event = JSON.parse(data) as CheckoutFrameMessage;
    return { kind: 'event', event, data };
  } catch (error) {
    return { kind: 'parseError', data, error };
  }
}
